import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import { claveIdempotencia } from '../../../api/operaciones';
import type { Cita, ConceptoPago, Pago, Reserva } from '../../../api/ops/pagos';
import { Baldosas } from '../../../componentes/Baldosas';
import { CampoImporte } from '../../../componentes/CampoImporte';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { Icono } from '../../../componentes/Icono';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { aCentimos, dinero, errorDeImporte } from '../../../logica/dinero';
import { avisoBreveDeError } from '../../../logica/estados';
import { hoyLocal } from '../../../logica/fechasCortas';
import { mayuscula, type NegocioPagos } from '../negocio';
import { textoDeError } from '../textos';

/**
 * «Registrar un pago» (4.7): lo que te pagan en el local, efectivo o datáfono del negocio, sin cierre de caja (G5, G6).
 * Se cobra **algo**: la reserva o la cita de hoy que se busca (`payable` obligatorio; G288: sin lista de «pendiente de
 * cobrar», y el importe se escribe). «Registrar 96,00 €» es la confirmación: sin «Deshacer» (G159, mueve dinero); si se
 * anotó mal, lo anula el propietario o un administrador. Mientras Reservas y Citas no existan, la búsqueda sale vacía.
 */
export interface Cobrable {
    tipo: 'reservation' | 'appointment';
    id: string;
    customerId: string | null;
    titulo: string;
    linea: string;
}

const hora = (iso: string) => {
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

function deReserva(r: Reserva, n: NegocioPagos): Cobrable {
    return {
        tipo: 'reservation',
        id: r.id,
        customerId: r.customer_id ?? null,
        titulo: `${mayuscula(n.compromiso === 'cita' ? 'reserva' : n.compromiso)} ${r.reference}`,
        linea: [r.local_time ?? hora(r.starts_at), `${String(r.party_size)} ${r.party_size === 1 ? 'persona' : 'personas'}`].filter((x) => x !== '').join(' · '),
    };
}

function deCita(c: Cita): Cobrable {
    return { tipo: 'appointment', id: c.id, customerId: c.customer_id, titulo: `Cita ${c.code}`, linea: c.local_time };
}

const CONCEPTOS: readonly { valor: ConceptoPago; texto: string }[] = [
    { valor: 'balance', texto: 'Resto' },
    { valor: 'deposit', texto: 'Señal' },
    { valor: 'sale', texto: 'Venta' },
];

export function Registrar({ ops, negocio, movil, abierto, n, onCerrar, onRegistrado, onError }: { ops: Operaciones; negocio: string; movil: boolean; abierto: boolean; n: NegocioPagos; onCerrar: () => void; onRegistrado: (pago: Pago, cobrable: Cobrable) => void; onError: (texto: string) => void }) {
    const [hoy, setHoy] = useState<Cobrable[] | null>(null);
    const [buscar, setBuscar] = useState('');
    const [elegido, setElegido] = useState<Cobrable | null>(null);
    const [forma, setForma] = useState<'cash' | 'card_terminal' | null>(null);
    const [importe, setImporte] = useState('');
    const [concepto, setConcepto] = useState<ConceptoPago>('balance');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [clave, setClave] = useState(claveIdempotencia);

    // Cada vez que se abre, un registro nuevo (con su propia clave de idempotencia).
    const [abiertoAntes, setAbiertoAntes] = useState(abierto);
    if (abierto !== abiertoAntes) {
        setAbiertoAntes(abierto);
        if (abierto) {
            setHoy(null);
            setBuscar('');
            setElegido(null);
            setForma(null);
            setImporte('');
            setConcepto('balance');
            setIntentado(false);
            setClave(claveIdempotencia());
        }
    }

    // Al abrir, las reservas y citas de hoy (cada una de su módulo; sin el módulo, ninguna).
    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vivo = true;
        const dia = hoyLocal();
        void Promise.all([
            ops.listReservations(negocio, { service_date: dia, limit: 100 }).then((l) => l.data.map((r) => deReserva(r, n))).catch(() => [] as Cobrable[]),
            ops.listAppointments(negocio, { from: `${dia}T00:00:00Z`, to: `${dia}T23:59:59Z`, limit: 100 }).then((l) => l.data.map(deCita)).catch(() => [] as Cobrable[]),
        ]).then(([r, c]) => {
            if (vivo) {
                setHoy([...r, ...c]);
            }
        });
        return () => {
            vivo = false;
        };
        // El vocabulario solo cambia los textos: no se vuelve a pedir la lista por él.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abierto, ops, negocio]);

    const q = buscar.trim().toLocaleLowerCase('es');
    const encontrados = (hoy ?? []).filter((c) => q === '' || `${c.titulo} ${c.linea}`.toLocaleLowerCase('es').includes(q));
    const centimos = aCentimos(importe);
    const errImporte = intentado ? errorDeImporte(importe, true) : null;
    const accion = centimos !== null && centimos > 0 ? `Registrar ${dinero({ amount: centimos, currency: n.moneda })}` : 'Registrar';

    const registrar = () => {
        setIntentado(true);
        if (elegido === null || forma === null || centimos === null || centimos <= 0) {
            return;
        }
        setOcupado(true);
        ops.registerExternalPayment(
            negocio,
            {
                payable: { type: elegido.tipo, id: elegido.id },
                ...(elegido.customerId === null ? {} : { customer_id: elegido.customerId }),
                concept: concepto,
                amount: { amount: centimos, currency: n.moneda },
                method: forma,
                recorded_at: new Date().toISOString(),
            },
            clave,
        )
            .then((pago) => {
                setOcupado(false);
                onRegistrado(pago, elegido);
            })
            .catch((e: unknown) => {
                setOcupado(false);
                onError(avisoBreveDeError(e) ?? textoDeError(e, 'No he podido registrar el pago. Prueba de nuevo.'));
            });
    };

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            titulo="Registrar un pago"
            eyebrow="Pago en el local"
            subtitulo="Efectivo o datáfono del negocio. Sin cierre de caja."
            icono="banknote"
            accion={accion}
            accionIcono="check"
            ocupado={ocupado}
            ocupadoTexto="Registrando…"
            cerrar="Cancelar"
            onAccion={registrar}
        >
            <div className="rv-ca-cuerpo">
                <div className="rv-ca-bloque">
                    <p className="rv-ca-ceja">Qué cobras</p>
                    {elegido === null ? (
                        <div className="rv-ca-buscar">
                            <Campo etiqueta="Buscar reserva o cita" tipo="search" valor={buscar} onChange={setBuscar} error={intentado ? 'Elige la reserva o la cita que te pagan.' : null} />
                            {hoy === null ? (
                                <span className="rv-ca-nota rv-ca-nota--gris">Un momento…</span>
                            ) : encontrados.length === 0 ? (
                                <span className="rv-ca-nota rv-ca-nota--gris">{q === '' ? 'No hay reservas ni citas de hoy.' : 'No encontramos esa reserva o cita de hoy.'}</span>
                            ) : (
                                <div className="rv-ca-resultados">
                                    {encontrados.slice(0, 20).map((c) => (
                                        <button key={`${c.tipo}${c.id}`} type="button" className="rv-ca-resultado" onClick={() => { setElegido(c); }}>
                                            <span className="rv-ca-baldosa"><Icono nombre={c.tipo === 'appointment' ? 'calendar' : 'calendar-check'} /></span>
                                            <span><strong>{c.titulo}</strong><small>{c.linea}</small></span>
                                            <Icono nombre="chevron-right" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="rv-ca-elegido">
                            <span className="rv-ca-baldosa"><Icono nombre={elegido.tipo === 'appointment' ? 'calendar' : 'calendar-check'} /></span>
                            <span><strong>{elegido.titulo}</strong><small>{elegido.linea}</small></span>
                            <button type="button" className="rv-b rv-b-sec" onClick={() => { setElegido(null); }}>
                                <Icono nombre="x" />
                                <span>Cambiar</span>
                            </button>
                        </div>
                    )}
                </div>
                <div className="rv-ca-bloque">
                    <p className="rv-ca-ceja">Cómo te paga</p>
                    <Baldosas
                        etiqueta="Cómo te paga"
                        opciones={[
                            { valor: 'cash', texto: 'Efectivo', icono: 'banknote' },
                            { valor: 'card_terminal', texto: 'Datáfono', icono: 'credit-card' },
                        ]}
                        valor={forma}
                        onCambio={setForma}
                    />
                    {intentado && forma === null && <span className="rv-campo__error" role="alert"><Icono nombre="circle-alert" /><span>Elige cómo te paga.</span></span>}
                </div>
                <CampoImporte etiqueta="Importe" moneda={n.moneda} valor={importe} onChange={setImporte} error={errImporte} ayuda="Lo que te ha pagado." />
                <Desplegable etiqueta="Concepto" valor={concepto} opciones={CONCEPTOS} onChange={(v) => { setConcepto(v as ConceptoPago); }} />
                <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="info" /><span>Solo se anota: Reservaria no cobra nada. Si te equivocas, lo anula el propietario o un administrador.</span></p>
            </div>
        </PanelLateral>
    );
}
